// Date helpers that AVOID the YYYY-MM-DD-as-UTC-midnight timezone trap.
//
// Background: `new Date("2026-08-01")` is parsed as UTC midnight per ECMAScript spec.
// In any negative-offset timezone (Americas), `.toLocaleDateString()` then renders
// the *previous* day. Travel dates are calendar dates and must NEVER shift.

/**
 * Parse a stored date string into a local Date.
 *  - "YYYY-MM-DD"           → local Date at 00:00 (no TZ shift)
 *  - "YYYY-MM-DDTHH:MM..."  → real instant (Date constructor)
 *  - falsy / unparseable    → null
 */
export function parseDate(s) {
  if (!s) return null;
  const str = String(s);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

const LONG_OPTS = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
const SHORT_OPTS = { month: "short", day: "numeric", year: "numeric" };

export function formatDate(s, opts = LONG_OPTS) {
  const d = parseDate(s);
  if (!d) return "";
  return d.toLocaleDateString(undefined, opts);
}

export function formatDateShort(s) {
  return formatDate(s, SHORT_OPTS);
}

export function formatTime(s) {
  if (!s) return "";
  // Standalone "HH:MM" or "HH:MM:SS" — render directly as 24h "HH:MM"
  const tm = String(s).match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (tm) {
    const h = parseInt(tm[1], 10);
    return `${String(h).padStart(2, "0")}:${tm[2]}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  // 24h "HH:MM"
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * Returns "+1d", "+2d", etc. when `endDate` lands a day or more after `startDate`.
 * Used for overnight flights and any cross-midnight scheduling so clients
 * instantly see "23:45 → 06:15 +1d" without doing date math.
 * Returns "" when both dates are the same day (or either is missing).
 * Both inputs are YYYY-MM-DD strings.
 */
export function dayOffsetLabel(startDate, endDate) {
  if (!startDate || !endDate) return "";
  const s = String(startDate).slice(0, 10);
  const e = String(endDate).slice(0, 10);
  if (s === e) return "";
  const sd = new Date(s + "T00:00:00Z");
  const ed = new Date(e + "T00:00:00Z");
  if (isNaN(sd.getTime()) || isNaN(ed.getTime())) return "";
  const diff = Math.round((ed - sd) / 86_400_000);
  if (diff <= 0) return "";
  return `+${diff}d`;
}

/**
 * Inclusive day count between two date-only strings.
 * Aug 1 → Aug 6 = 6 days, 5 nights.
 */
export function tripDayCount(startStr, endStr) {
  const s = parseDate(startStr);
  const e = parseDate(endStr);
  if (!s || !e) return null;
  const ms = e - s;
  return Math.max(1, Math.round(ms / 86400000) + 1);
}

export function tripNightCount(startStr, endStr) {
  const days = tripDayCount(startStr, endStr);
  return days == null ? null : Math.max(0, days - 1);
}
